import { ArrowsClockwise } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { haptics } from '@shared/lib/telegram';
import { apiErrorMessage } from '@shared/api/baseApi';
import {
  orderApi,
  useCancelActivationMutation,
  useCheckActivationMutation,
  useGetOrderQuery,
  type OrderDto,
} from '../../api/orderApi';
import styles from './ActivationPanel.module.css';

/**
 * How often the order is re-read while a code is in flight.
 *
 * Matched to the server's own sweep rather than made as fast as possible:
 * polling faster than the poller settles orders only adds requests that see
 * the same state twice.
 */
const POLL_MS = 5_000;

/** Under this much left, the countdown turns urgent. */
const URGENT_MS = 60_000;

/**
 * The live half of a number purchase.
 *
 * A rented number is the one thing the shop sells that is not finished when it
 * is bought: the buyer takes the number away, types it into WhatsApp or
 * whoever, and the code lands here minutes later — or never, in which case the
 * server refunds them without being asked.
 *
 * So this screen exists to answer the two questions that state raises: what is
 * my number, and how long have I got. It polls the single-order endpoint until
 * the status moves off AWAITING_SMS, at which point the parent re-renders it
 * as an ordinary delivered or refunded order.
 */
export function ActivationPanel({ order }: { order: OrderDto }) {
  const dispatch = useDispatch();
  const [settled, setSettled] = useState(false);

  // Only orders that arrived live are watched at all: without the skip, every
  // settled number in the history would hold a poll open for as long as the
  // list is on screen.
  const watching = order.status === 'AWAITING_SMS';
  const { data: polled } = useGetOrderQuery(order.id, {
    skip: !watching,
    // Stopped from the polled copy, never from the prop. The prop comes from
    // the orders list, which nothing refreshes on its own — polling on it
    // would carry on forever after the code had already arrived.
    pollingInterval: watching && !settled ? POLL_MS : 0,
  });

  const current = polled ?? order;

  // The code landed, or the number lapsed and was refunded. The list holding
  // this card knows neither, so it is refreshed once — that is what puts the
  // delivered code on the card and moves it off "Waiting for code".
  useEffect(() => {
    if (settled || !polled || polled.status === 'AWAITING_SMS') return;

    setSettled(true);
    dispatch(orderApi.util.invalidateTags(['Order', 'User']));
  }, [polled, settled, dispatch]);

  const [cancelActivation, { isLoading: cancelling }] = useCancelActivationMutation();
  const [checkActivation, { isLoading: checking }] = useCheckActivationMutation();
  const [error, setError] = useState<string | null>(null);

  /**
   * Brief cooldown after a manual check.
   *
   * Each tap is a real request to SMSPool, and their docs call `sms/check`
   * rate-limited under polling — so the button stops someone tapping it
   * twenty times while they wait, which is exactly what people do.
   */
  const [cooldown, setCooldown] = useState(false);

  useEffect(() => {
    if (!cooldown) return;

    const timer = setTimeout(() => setCooldown(false), 4_000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const remaining = useCountdown(current.activation?.expiresAt ?? null, current.status === 'AWAITING_SMS');

  if (!current.activation) return null;

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(current.activation!.phoneNumber);
      haptics.notify('success');
    } catch {
      haptics.notify('error');
    }
  };

  const check = async () => {
    setError(null);
    haptics.tap('light');
    setCooldown(true);
    try {
      await checkActivation(current.id).unwrap();
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not check for the code.'));
    }
  };

  const cancel = async () => {
    setError(null);
    haptics.tap('medium');
    try {
      await cancelActivation(current.id).unwrap();
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not release this number.'));
    }
  };

  const expired = remaining !== null && remaining <= 0;

  return (
    <div className={styles.panel}>
      <p className={styles.label}>Your number</p>
      <button type="button" className={styles.number} onClick={() => void copyNumber()}>
        {formatNumber(current.activation.phoneNumber)}
      </button>
      <p className={styles.hint}>Tap to copy · enter it where you need the code</p>

      {current.status === 'AWAITING_SMS' ? (
        <>
          <div
            className={
              remaining !== null && remaining <= URGENT_MS
                ? `${styles.waiting} ${styles.urgent}`
                : styles.waiting
            }
          >
            <span className={styles.pulse} aria-hidden />
            <span>
              {/* The countdown is the whole point: an expired number refunds
                  itself, so a buyer who knows the time left knows whether to
                  keep waiting or release it and try another country. */}
              {expired
                ? 'Time is up — refunding your balance…'
                : `Waiting for the code · ${formatRemaining(remaining)}`}
            </span>
          </div>

          {/* Waiting with no way to prod it is its own kind of broken, even
              when the sweep would have found the same thing seconds later. */}
          <button
            type="button"
            className={styles.check}
            onClick={() => void check()}
            disabled={checking || cooldown || expired}
          >
            <ArrowsClockwise size={15} weight="bold" className={checking ? styles.spin : undefined} />
            {checking ? 'Checking…' : cooldown ? 'Checked just now' : 'Check for code'}
          </button>

          {/* Offered because a number a service refuses to text is dead weight
              for twenty minutes with the buyer's money behind it. */}
          <button
            type="button"
            className={styles.cancel}
            onClick={() => void cancel()}
            disabled={cancelling || expired}
          >
            {cancelling ? 'Releasing…' : 'Release and refund'}
          </button>
        </>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

/**
 * Milliseconds left, ticking once a second.
 *
 * Recomputed from the absolute expiry on every tick rather than decremented,
 * so a phone that slept through half the window shows the truth when it wakes
 * instead of a countdown frozen where it stopped.
 */
function useCountdown(expiresAt: string | null, active: boolean): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt || !active) return;

    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [expiresAt, active]);

  if (!expiresAt) return null;

  return new Date(expiresAt).getTime() - now;
}

function formatRemaining(ms: number | null): string {
  if (ms === null) return '';

  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')} left`;
}

/** Grouped for reading aloud and for spotting a typo before it is submitted. */
function formatNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 7) return raw;

  return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10, -7)} ${digits.slice(-7, -4)} ${digits.slice(-4)}`.trim();
}
